import { type Locale } from '../i18n'
import { ROUTES, localizedPath, type StaticPageKey } from '../i18n/routes'
import { SECTOR_BASE, getSectorById, type SectorId } from '../data/sectors'
import { getArticleByLocaleSlug, articleSlug } from '../data/articles'

/**
 * Map FR static path → StaticPageKey, derived from the route registry.
 * The home page ('' slug) maps from '/'.
 */
const FR_PATH_TO_KEY: Record<string, StaticPageKey> = (() => {
  const map: Record<string, StaticPageKey> = {}
  for (const key of Object.keys(ROUTES) as StaticPageKey[]) {
    const frSlug = ROUTES[key].fr
    const frPath = frSlug === '' ? '/' : `/${frSlug}`
    map[frPath] = key
  }
  return map
})()

/** FR slug of a sector list page (e.g. 'secteurs'). */
const FR_SECTORS_SLUG = ROUTES.sectors.fr
/** FR slug of a blog list page (e.g. 'blog'). */
const FR_BLOG_SLUG = ROUTES.blog.fr

/**
 * Resolves a single FR internal path to its localized equivalent, or returns
 * `null` if the path is not a known FR route (caller leaves it untouched).
 */
function resolveFrPath(path: string, locale: Locale): string | null {
  // Known static path (exact match)?
  const staticKey = FR_PATH_TO_KEY[path]
  if (staticKey) return localizedPath(staticKey, locale)

  // /secteurs/<frSlug> → sectors.detail with localized sector slug.
  const sectorPrefix = `/${FR_SECTORS_SLUG}/`
  if (path.startsWith(sectorPrefix)) {
    const frSlug = path.slice(sectorPrefix.length)
    if (frSlug && !frSlug.includes('/')) {
      const base = SECTOR_BASE.find((b) => b.slug.fr === frSlug)
      if (base) {
        const resolved = getSectorById(base.id as SectorId)
        if (resolved) {
          return localizedPath('sectors.detail', locale, {
            slug: resolved.slug[locale],
          })
        }
      }
    }
    return null
  }

  // /blog/<frArticleSlug> → blog.article with localized article slug.
  const blogPrefix = `/${FR_BLOG_SLUG}/`
  if (path.startsWith(blogPrefix)) {
    const frSlug = path.slice(blogPrefix.length)
    if (frSlug && !frSlug.includes('/')) {
      const article = getArticleByLocaleSlug('fr', frSlug)
      if (article) {
        return localizedPath('blog.article', locale, {
          slug: articleSlug(article, locale),
        })
      }
    }
    return null
  }

  return null
}

/**
 * Rewrites internal `href="/..."` (and `href='/...'`) in an HTML string to
 * localized paths, for KNOWN FR routes only. Conservative: anchors, external
 * links, mailto:/tel: and any unknown internal path are left untouched.
 *
 * For `locale === 'fr'` this is a no-op (paths are already FR).
 */
export function localizeHtmlLinks(html: string, locale: Locale): string {
  if (locale === 'fr') return html

  // Match href="(/path)" or href='(/path)' where the path has no '#' (anchors)
  // and starts with a single '/' (not '//', i.e. not protocol-relative).
  const re = /href=("|')(\/[^"'#]*)\1/g
  return html.replace(re, (match, quote: string, path: string) => {
    // Protocol-relative (//host) → leave untouched.
    if (path.startsWith('//')) return match
    const resolved = resolveFrPath(path, locale)
    if (resolved === null) return match
    return `href=${quote}${resolved}${quote}`
  })
}

/** Locale tag for `Date.toLocaleDateString`. */
const DATE_LOCALE_TAG: Record<Locale, string> = {
  fr: 'fr-BE',
  en: 'en-GB',
  nl: 'nl-NL',
}

/**
 * Formats an ISO date string ("YYYY-MM-DD") into a long localized date.
 * FR output matches the legacy `formatDate` ('fr-BE' + day/month/year).
 */
export function formatDateLocalized(iso: string, locale: Locale): string {
  // timeZone:'UTC' is REQUIRED: the ISO is a date-only string (parsed as UTC
  // midnight). Without a fixed zone, the SSG prerender (Node, UTC) and the
  // browser (user's local TZ) can format different calendar days near the
  // boundary → React hydration mismatch (#418) on every date on /blog. Pinning
  // UTC makes server and client output byte-identical.
  return new Date(iso).toLocaleDateString(DATE_LOCALE_TAG[locale], {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
